import React, { useMemo, useState } from 'react';
import { Loader2, ShieldAlert, X } from 'lucide-react';
import { ProjectCheckConfig, ProjectCheckResult, ProjectCheckTraffic } from '../../types';
import { Button } from '../Button';
import { sweepAllProjects } from '../../services/projectCheckService';
import { isChildProject } from '../../services/projectLv';
import { isProjectArchived } from '../../services/projectBilling';
import type { ListProject } from '../../services/listProject';
import { ProjectCheckSweepRow } from './ProjectCheckPanel';

/**
 * Read-only-Lauf des Projektchecks ueber den Bestand.
 *
 * Vor der ersten echten Rechnung soll sichtbar sein, wie viel Drift in den
 * Altdaten steckt — der nicht-idempotente Vorankuendigungspfad und
 * Leistungsstandkorrekturen ohne Logeintrag sind bekannte Quellen. Der Lauf
 * schreibt nichts; er ist eine Diagnose, keine Bereinigung.
 *
 * Bewusst sequenziell: jedes Projekt zieht Projektdaten, Berichtsarchiv und
 * Reservierungen. Parallel waere es schneller und wuerde die Datenbank bei
 * einigen hundert Projekten mit einem Schlag fluten.
 */

interface Props {
    projects: ListProject[];
    config?: ProjectCheckConfig;
    today: string;
    onClose: () => void;
    onOpenProject?: (project: ListProject) => void;
}

export const ProjectCheckSweep: React.FC<Props> = ({
    projects,
    config,
    today,
    onClose,
    onOpenProject,
}) => {
    const [results, setResults] = useState<
        Array<{ projectId: string; result: ProjectCheckResult }>
    >([]);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<ProjectCheckTraffic | 'all'>('all');

    // Unterprojekte werden nie direkt abgerechnet; sie wuerden den Bericht mit
    // je einem roten Befund fluten, der nichts ueber die Datenqualitaet sagt.
    // Archivierte Projekte sind abgerechnet und bezahlt — dort wird nichts
    // mehr gestellt, ihre Befunde waeren nur Rauschen.
    const candidates = useMemo(
        () => projects.filter((p) => !isChildProject(p) && !isProjectArchived(p)),
        [projects],
    );
    const archivedCount = useMemo(
        () => projects.filter((p) => !isChildProject(p) && isProjectArchived(p)).length,
        [projects],
    );

    const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

    const start = async () => {
        setRunning(true);
        setError(null);
        setResults([]);
        setProgress({ done: 0, total: candidates.length });
        try {
            const out = await sweepAllProjects(
                candidates.map((p) => p.id),
                config,
                today,
                (done, total) => setProgress({ done, total }),
            );
            setResults(out);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Der Lauf ist fehlgeschlagen.');
        } finally {
            setRunning(false);
        }
    };

    const summary = useMemo(() => {
        let red = 0;
        let yellow = 0;
        let green = 0;
        for (const r of results) {
            if (r.result.traffic === 'red') red += 1;
            else if (r.result.traffic === 'yellow') yellow += 1;
            else green += 1;
        }
        return { red, yellow, green };
    }, [results]);

    const visible = useMemo(
        () => (filter === 'all' ? results : results.filter((r) => r.result.traffic === filter)),
        [results, filter],
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-4">
            <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                        <ShieldAlert className="h-5 w-5 text-slate-400" />
                        Projektcheck über den Bestand
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="border-b border-slate-200 px-5 py-3">
                    <p className="text-sm text-slate-600">
                        Prüft {candidates.length}{' '}
                        {candidates.length === 1 ? 'Projekt' : 'Projekte'} gegen alle 19 Regeln.{' '}
                        {archivedCount > 0 && (
                            <>
                                {archivedCount} archivierte{' '}
                                {archivedCount === 1 ? 'bleibt' : 'bleiben'} außen vor.{' '}
                            </>
                        )}
                        <span className="font-medium">Es wird nichts geändert.</span>
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                        <Button onClick={() => void start()} disabled={running}>
                            {running ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {progress.done} von {progress.total}
                                </>
                            ) : (
                                'Lauf starten'
                            )}
                        </Button>
                        {results.length > 0 && !running && (
                            <div className="flex gap-2 text-xs">
                                {(
                                    [
                                        ['all', `Alle (${results.length})`],
                                        ['red', `Gesperrt (${summary.red})`],
                                        ['yellow', `Prüfen (${summary.yellow})`],
                                        ['green', `Sauber (${summary.green})`],
                                    ] as Array<[ProjectCheckTraffic | 'all', string]>
                                ).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setFilter(key)}
                                        className={`rounded-full border px-2 py-0.5 ${
                                            filter === key
                                                ? 'border-slate-800 bg-slate-800 text-white'
                                                : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                </div>

                <div className="flex-1 overflow-auto">
                    {results.length === 0 && !running && (
                        <p className="px-5 py-6 text-sm text-slate-500">
                            Noch kein Lauf. Der Bericht zeigt danach je Projekt die Ampel und die
                            ersten Befunde.
                        </p>
                    )}
                    {visible.length > 0 && (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="px-3 py-2">Ampel</th>
                                    <th className="px-3 py-2">Projekt</th>
                                    <th className="px-3 py-2">Befunde</th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(({ projectId, result }) => {
                                    const project = byId.get(projectId);
                                    return (
                                        <ProjectCheckSweepRow
                                            key={projectId}
                                            projectName={project?.name ?? projectId}
                                            result={result}
                                            onOpen={
                                                project && onOpenProject
                                                    ? () => onOpenProject(project)
                                                    : undefined
                                            }
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};
